import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Cell,
} from "recharts";
import { theme, styles } from "./theme";

const LABEL_MAX_CHARS = 14;
const LABEL_FONT_SIZE = 11;

/** Split into 1, 2, or 3 lines at word boundaries. maxLines 3 when many campaigns for a cleaner look. */
function splitCampaignLabel(
  text: string,
  maxChars: number = LABEL_MAX_CHARS,
  maxLines: number = 2
): string[] {
  const t = (text || "").trim();
  if (!t) return [""];
  if (t.length <= maxChars) return [t];
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [t];
  if (maxLines >= 3 && words.length >= 3) {
    const n1 = Math.ceil(words.length / 3);
    const n2 = Math.ceil((2 * words.length) / 3);
    return [
      words.slice(0, n1).join(" "),
      words.slice(n1, n2).join(" "),
      words.slice(n2).join(" "),
    ];
  }
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

function getLabelGap(): number {
  if (typeof window === "undefined") return 10;
  const w = window.innerWidth;
  if (w >= 1024) return 10;
  if (w >= 768) return 8;
  return 6;
}

const ResponsiveContainerC = ResponsiveContainer as any;
const BarChartC = BarChart as any;
const CartesianGridC = CartesianGrid as any;
const XAxisC = XAxis as any;
const YAxisC = YAxis as any;
const TooltipC = Tooltip as any;
const LegendC = Legend as any;
const BarC = Bar as any;
const CellC = Cell as any;

interface CampaignData {
  campaign_name: string;
  leads_count: number;
  campaign_id?: string | number;
  id?: string | number;
  type_of_campaign?: string;
}

interface LeadsPerCampaignChartProps {
  data: CampaignData[];
  /** When user clicks a bar (campaign), call with campaign_id and campaign_name for click-to-expand. */
  onBarClick?: (campaignId: string | number, campaignName: string) => void;
}

// Custom tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(13, 148, 136, 0.2)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: 600,
            color: "#1f2937",
            marginBottom: "6px",
            maxWidth: "200px",
            wordBreak: "break-word",
          }}
        >
          {(label ?? "").replace(/_/g, " ")}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "#0d9488",
            fontWeight: 500,
          }}
        >
          Leads: <strong>{payload[0].value}</strong>
        </p>
      </div>
    );
  }
  return null;
};

// Custom X-axis tick: fixed font size, 1–3 lines so labels look perfect
const CustomXAxisTick = (props: any) => {
  const { x, y, payload, dataLength } = props;
  const n = dataLength ?? 0;
  const [labelGap, setLabelGap] = useState(() => getLabelGap());
  useEffect(() => {
    const onResize = () => setLabelGap(getLabelGap());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const value = (payload?.value ?? "").trim().replace(/_/g, " ");
  const maxChars = n <= 3 ? 999 : n >= 6 ? 10 : n >= 4 ? 12 : LABEL_MAX_CHARS;
  const maxLines = n > 4 ? 3 : 2;
  const lines = splitCampaignLabel(value, maxChars, maxLines);
  const fill = theme.chartColors?.axisText ?? "#94a3b8";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={labelGap}
        textAnchor="middle"
        fill={fill}
        fontSize={LABEL_FONT_SIZE}
        fontWeight={500}
        style={{ fontFamily: "inherit" }}
      >
        {lines.length === 1 ? (
          lines[0]
        ) : lines.length === 2 ? (
          <>
            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
          </>
        ) : (
          <>
            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[2]}</tspan>
          </>
        )}
      </text>
    </g>
  );
};

// Custom legend
const renderLegend = (props: any) => {
  const { payload } = props;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "16px",
        marginBottom: "8px",
      }}
    >
      {payload.map((entry: any, index: number) => (
        <div
          key={`legend-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            color: theme.colors.textSecondary,
            fontWeight: 500,
          }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "3px",
              backgroundColor: entry.color,
            }}
          />
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const LeadsPerCampaignChart: React.FC<LeadsPerCampaignChartProps> = ({ data = [], onBarClick }) => {
  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <div style={styles.emptyState as React.CSSProperties}>
        <div style={styles.emptyStateIcon as React.CSSProperties}>📊</div>
        <span>No campaign data available</span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onBarClick ? "pointer" : undefined,
      }}
    >
      <ResponsiveContainerC width="100%" height={260}>
        <BarChartC
          data={data}
          margin={{
            top: 10,
            right: 52,
            bottom: data.length > 4 ? 64 : 56,
            left: data.length >= 6 ? 44 : 36,
          }}
          barCategoryGap={data.length >= 8 ? "32%" : data.length > 6 ? "30%" : data.length > 4 ? "28%" : "25%"}
        >
          <defs>
            <linearGradient id="barGradientLeadsPerCampaign" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0d9488" stopOpacity={1} />
              <stop offset="100%" stopColor="#0d9488" stopOpacity={0.8} />
            </linearGradient>
          </defs>

          <CartesianGridC
            stroke={theme.chartColors.gridLine}
            strokeDasharray="0"
            vertical={false}
          />

          <XAxisC
            dataKey="campaign_name"
            axisLine={{ stroke: theme.chartColors.axisLine, strokeWidth: 1 }}
            tickLine={false}
            tick={<CustomXAxisTick dataLength={data.length} />}
            tickMargin={8}
            interval={0}
            height={data.length > 4 ? 64 : 48}
          />

          <YAxisC
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            tick={{ fontSize: 11, fill: theme.chartColors.axisText, fontWeight: 500 }}
            width={32}
          />

          <TooltipC
            content={<CustomTooltip />}
            cursor={false}
            isAnimationActive={false}
          />

          <LegendC
            content={renderLegend}
            verticalAlign="top"
            height={30}
          />

          <BarC
            dataKey="leads_count"
            fill="url(#barGradientLeadsPerCampaign)"
            radius={[4, 4, 0, 0]}
            maxBarSize={45}
            minPointSize={24}
            name="Number of leads"
            animationBegin={0}
            animationDuration={800}
            animationEasing="ease-out"
            onClick={onBarClick ? (entry: any, index: number) => {
              const row = entry?.payload ?? entry;
              let id = row?.campaign_id ?? row?.id;
              let name = row?.campaign_name ?? "";
              if ((id === undefined || id === null) && typeof index === "number" && index >= 0 && index < data.length) {
                const fallback = data[index];
                id = fallback?.campaign_id ?? fallback?.id;
                name = fallback?.campaign_name ?? "";
              }
              if (id !== undefined && id !== null) {
                onBarClick(id, name || "");
              }
            } : undefined}
            style={{ cursor: onBarClick ? "pointer" : undefined }}
          >
            {data.map((row: any, index: number) => (
              <CellC
                key={`cell-${index}`}
                fill="#0d9488"
                style={{
                  filter: "drop-shadow(0 2px 4px rgba(13, 148, 136, 0.2))",
                  cursor: onBarClick ? "pointer" : undefined,
                }}
              />
            ))}
          </BarC>
        </BarChartC>
      </ResponsiveContainerC>
    </div>
  );
};

export default LeadsPerCampaignChart;
